import { selectUserInfo } from "features/user/userSlice";
import { Grid, TextField } from "@mui/material"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RoleProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Roles } from "assets/SideBarIcons/roles.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useState } from "react";
import { GetRoleByIdResponse } from "subModule/src/services/role/interface";
import Loader from "layout/Loader";
import Roletabs from "./Roletabs";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";

interface FormData { [key: string]: any;}
export default function RolesModal() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const { register, handleSubmit, formState: { errors, dirtyFields, isDirty }, reset } = useForm<FormData>({defaultValues: {}});
    const loggedInUserData = useSelector(selectUserInfo);
    const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
    const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
    const isArabicLanguage = (lang?: string) => {
        const l = (lang || "").toLowerCase();
        return l === "arabic" || l === "ar" || l.includes("arab");
    };
    const localizedRoleNamePlaceholder = (lang?: string) =>
        isArabicLanguage(lang) ? t("phRoleNameAr") : t("phRoleNameEn");
    const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
    const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(params?.id ? false : true);
    const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
        sectionLabelKey: "general",
        moduleLabelKey: "Roles",
        moduleHref: "/roles",
        entityFallbackLabelKey: "title",
        isEdit: !!params?.id,
    });
    useEffect(() => {
        if(params?.id) {
            if(loading) getByID();
        }
    }, [params]);
    useEffect(() => {
        if (isDirty && params?.id) setIsBtnEnabled(true);
      }, [isDirty, dirtyFields]);
    const getByID = () => {
        RoleProfileService.getRoleByID(+(params as any)?.id).then((res: GetRoleByIdResponse) => {
            if(res.success) {
                reset({ ...res?.data || {} });
                captureLabel(res?.data?.title);
                setLoading(false);
            } else {
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err:any) => { console.error("===", err); });
    }
    const onSubmit = (data: any) => {
        setIsBtnEnabled(false);
        if(params?.id) {
            RoleProfileService.updateRole(+params?.id, {...data}).then((response: any) => {
                if(response.success) {
                    captureLabel(data?.title);
                    getByID();
                    enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('role') + ' ' + t('updated') + ' ' + t('successfully')} />, { variant: 'success' });
                } else {
                    enqueueSnackbar(<AlertMessages text={t(response?.message)} />, { variant: 'error' });
                    setIsBtnEnabled(true);
                }
            }).catch((err:any) => { 
                console.error("===", err);
                setIsBtnEnabled(true);
                enqueueSnackbar(<AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />, { variant: 'error' });
            });
        } else {
            RoleProfileService.createRole(data).then((response: any) => {
                if(response.success) {
                    enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('role') + ' ' + t('created') + ' ' + t('successfully')} />, { variant: 'success' });
                    navigate('/roles');
                } else {
                    setIsBtnEnabled(true);
                    enqueueSnackbar(<AlertMessages text={t(response?.message)} />, { variant: 'error' });
                }
            }).catch((err:any) => { 
                console.error("===", err);
                setIsBtnEnabled(true);
                enqueueSnackbar(<AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />, { variant: 'error' });
            });
        }
    };

    return (<>
        <ComponentHeaderTwo
            showBtn={true}
            showSaveBtn={true}
            onClick={() => navigate('/roles')}
            onSubmit={handleSubmit(onSubmit)}
            heading={params?.id ? t('editRoles') : t('addRoles')}
            icon={<Roles height={28} width={28} />}
            btnText={"back"}
            btnType={"back"}
            isBtnEnabled={IsBtnEnabled}
            showCreateBtn={checkPrivileges("ROLE_ROLE_CREATE")}
            breadcrumbItems={breadcrumbItems}
        />
        <div className={`${params?.id ? 'flex' : ''}`}>
            {params?.id ? <Roletabs params={params} selectedIndex={0} /> : null}
            {loading ? <Loader /> : <SimpleModal
                visable={true}
                title={params?.id ? t('editRoles') : t('addRoles')}
                isButtonVisable={true}
                isVisableBtn={IsBtnEnabled}
                btnclosetext="Back"
                buttonText={params?.id ? "update" : "submit"}
                attBtnNotshow={false}
                notVisableBackbtn={true}
                showPortList={handleSubmit(onSubmit)}
                formSubmit={handleSubmit(onSubmit)}
                content={ 
                    <Grid container component="form" id="form" className="items-baseline mb-2" spacing={3} onSubmit={handleSubmit(onSubmit)}>
                        <Grid item xs={12} md={3}>
                            <TextField 
                                {...register("title", { validate: { required: (value) => value.trim() !== '' || t('This field is required.'), } })} 
                                label={`${!primaryLanguage ? t('primary') : ''} ${t('title')} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ''} *`}
                                placeholder={localizedRoleNamePlaceholder(primaryLanguage)}
                                title={(`${!primaryLanguage ? t('primary') : ''} ${t('title')} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ''} *`).replace(/\s+/g, ' ').trim()}
                                fullWidth
                                error={!!errors.title} 
                                helperText={errors.title?.message as string} 
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField 
                                {...register("secondaryTitle")} 
                                label={`${!secondaryLanguage ? t('secondary') : ''} ${t('title')} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ''}`}
                                placeholder={localizedRoleNamePlaceholder(secondaryLanguage)}
                                title={(`${!secondaryLanguage ? t('secondary') : ''} ${t('title')} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ''}`).replace(/\s+/g, ' ').trim()}
                                fullWidth
                                error={!!errors.secondaryTitle} 
                                helperText={errors.secondaryTitle?.message as string} 
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField 
                                {...register("description")} 
                                label={t('description')}
                                placeholder={t('phRoleDescription')}
                                title={t('description')}
                                fullWidth
                                error={!!errors.description} 
                                helperText={errors.description?.message as string}
                                InputLabelProps={{ shrink: true }}
                                multiline
                                rows={1}
                                sx={{ 
                                    '& .MuiInputBase-fullWidth.MuiInputBase-multiline': { width: '100%', paddingRight: '0px', paddingBottom: 0 },
                                    '& .MuiInputBase-inputMultiline': { resize: 'vertical', minHeight: '29px' }
                                }}
                            />
                        </Grid>
                    </Grid>
                }
            />}
        </div>
    </>)
}